import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ImagePlus, Trash2, GripVertical, Upload, X, Loader2, 
  Image as ImageIcon, Edit2, Check, Plane
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery?category=aircraft`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      toast.error('Görseller yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = files.filter(file => 
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)
    );

    if (validFiles.length !== files.length) {
      toast.error('Bazı dosyalar geçersiz format. Sadece JPEG, PNG, WebP ve GIF desteklenir.');
    }

    const newPreviews = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      title: '',
      description: ''
    }));

    setPreviewFiles(prev => [...prev, ...newPreviews]);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, []);

  const removePreview = (index) => {
    setPreviewFiles(prev => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index].preview);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const updatePreviewTitle = (index, title) => {
    setPreviewFiles(prev => {
      const newPreviews = [...prev];
      newPreviews[index].title = title;
      return newPreviews;
    });
  };

  const uploadImages = async () => {
    if (previewFiles.length === 0) return;

    setUploading(true);
    const token = getToken();
    let successCount = 0;

    for (const item of previewFiles) {
      const formData = new FormData();
      formData.append('file', item.file);
      formData.append('category', 'aircraft');
      if (item.title) formData.append('title', item.title);
      if (item.description) formData.append('description', item.description);

      try {
        const response = await fetch(`${API_BASE_URL}/api/gallery`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          successCount++;
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    // Clear previews
    previewFiles.forEach(p => URL.revokeObjectURL(p.preview));
    setPreviewFiles([]);
    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} görsel başarıyla yüklendi`);
      fetchImages();
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Bu görseli silmek istediğinize emin misiniz?')) return;

    const token = getToken();
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Görsel silindi');
        setImages(prev => prev.filter(img => img.id !== imageId));
      } else {
        toast.error('Görsel silinemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const updateImage = async (imageId, data) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery/${imageId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success('Görsel güncellendi');
        fetchImages();
        setEditingImage(null);
      } else {
        toast.error('Güncelleme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const getImageUrl = (filename) => `${API_BASE_URL}/api/gallery/file/${filename}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={32} style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: '#F8FAFC' }}>
            <Plane size={28} style={{ color: '#00F0FF' }} />
            Araç Galerisi
          </h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
            Aircraft sayfasındaki galeri görsellerini yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: '#94A3B8' }}>
            {images.length} görsel
          </span>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`rounded-xl p-8 transition-all duration-300 ${dragOver ? 'scale-[1.02]' : ''}`}
        style={{
          background: dragOver ? 'rgba(0, 240, 255, 0.15)' : 'rgba(30, 41, 59, 0.6)',
          border: dragOver ? '2px dashed #00F0FF' : '2px dashed rgba(0, 240, 255, 0.3)'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="upload-dropzone"
      >
        <div className="text-center">
          <Upload size={48} className="mx-auto mb-4" style={{ color: '#00F0FF' }} />
          <p className="text-lg font-medium mb-2" style={{ color: '#F8FAFC' }}>
            Görselleri Sürükle & Bırak
          </p>
          <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
            veya bilgisayarınızdan seçin
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 rounded-lg font-medium transition-all"
            style={{
              background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
              color: '#0F172A'
            }}
            data-testid="select-files-btn"
          >
            <ImagePlus size={18} className="inline mr-2" />
            Dosya Seç
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
          />
          <p className="text-xs mt-4" style={{ color: '#64748B' }}>
            Desteklenen formatlar: JPEG, PNG, WebP, GIF
          </p>
        </div>
      </div>

      {/* Preview Area */}
      {previewFiles.length > 0 && (
        <div className="rounded-xl p-6" style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(0, 240, 255, 0.2)'
        }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium" style={{ color: '#F8FAFC' }}>
              Yüklenecek Görseller ({previewFiles.length})
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  previewFiles.forEach(p => URL.revokeObjectURL(p.preview));
                  setPreviewFiles([]);
                }}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ background: 'rgba(100, 116, 139, 0.3)', color: '#94A3B8' }}
              >
                Temizle
              </button>
              <button
                onClick={uploadImages}
                disabled={uploading}
                className="px-6 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
                  color: '#0F172A'
                }}
                data-testid="upload-btn"
              >
                {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                {uploading ? 'Yükleniyor...' : 'Tümünü Yükle'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {previewFiles.map((item, index) => (
              <div key={index} className="relative group rounded-lg overflow-hidden" style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.2)'
              }}>
                <img
                  src={item.preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-40 object-cover"
                />
                <button
                  onClick={() => removePreview(index)}
                  className="absolute top-2 right-2 p-1 rounded-full transition-opacity opacity-0 group-hover:opacity-100"
                  style={{ background: 'rgba(239, 68, 68, 0.9)' }}
                >
                  <X size={16} style={{ color: '#FFF' }} />
                </button>
                <div className="p-3">
                  <input
                    type="text"
                    placeholder="Başlık (opsiyonel)"
                    value={item.title}
                    onChange={(e) => updatePreviewTitle(index, e.target.value)}
                    className="w-full px-3 py-2 rounded text-sm outline-none"
                    style={{
                      background: 'rgba(15, 23, 42, 0.8)',
                      border: '1px solid rgba(0, 240, 255, 0.2)',
                      color: '#F8FAFC'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="rounded-xl p-6" style={{
        background: 'rgba(30, 41, 59, 0.6)',
        border: '1px solid rgba(0, 240, 255, 0.2)'
      }}>
        <h3 className="text-lg font-medium mb-4" style={{ color: '#F8FAFC' }}>
          Mevcut Görseller
        </h3>

        {images.length === 0 ? (
          <div className="text-center py-12" style={{ color: '#64748B' }}>
            <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
            <p>Henüz görsel yüklenmemiş</p>
            <p className="text-sm mt-2">Yukarıdaki alana görselleri sürükleyip bırakın</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div 
                key={image.id} 
                className="relative group rounded-lg overflow-hidden"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.2)'
                }}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={getImageUrl(image.filename)}
                  alt={image.title || image.original_name}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
                
                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => setEditingImage(image)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ background: 'rgba(0, 240, 255, 0.3)', color: '#00F0FF' }}
                    title="Düzenle"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => deleteImage(image.id)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ background: 'rgba(239, 68, 68, 0.3)', color: '#EF4444' }}
                    title="Sil"
                    data-testid={`delete-image-${image.id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Image Info */}
                <div className="p-3">
                  <p className="text-sm truncate" style={{ color: '#F8FAFC' }}>
                    {image.title || image.original_name}
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#64748B' }}>
                    {new Date(image.created_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl p-6" style={{
            background: '#1E293B',
            border: '1px solid rgba(0, 240, 255, 0.3)'
          }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold" style={{ color: '#F8FAFC' }}>
                Görsel Düzenle
              </h2>
              <button onClick={() => setEditingImage(null)} style={{ color: '#94A3B8' }}>
                <X size={24} />
              </button>
            </div>

            <img
              src={getImageUrl(editingImage.filename)}
              alt={editingImage.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  Başlık
                </label>
                <input
                  type="text"
                  value={editingImage.title || ''}
                  onChange={(e) => setEditingImage({...editingImage, title: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg outline-none"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                  Açıklama
                </label>
                <textarea
                  value={editingImage.description || ''}
                  onChange={(e) => setEditingImage({...editingImage, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg outline-none resize-none"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    color: '#F8FAFC'
                  }}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingImage(null)}
                className="flex-1 px-4 py-3 rounded-lg font-medium"
                style={{ background: 'rgba(100, 116, 139, 0.2)', color: '#94A3B8' }}
              >
                İptal
              </button>
              <button
                onClick={() => updateImage(editingImage.id, {
                  title: editingImage.title,
                  description: editingImage.description
                })}
                className="flex-1 px-4 py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #00F0FF 0%, #0066FF 100%)',
                  color: '#0F172A'
                }}
              >
                <Check size={18} />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
